import { ALL_AVATARS, type AvatarKey } from '@hazel/shared'

import { Avatar } from './Avatar.js'

export interface TeddyChoiceProps {
  readonly chosen: AvatarKey | null
  readonly onChoose: (teddy: AvatarKey) => void
  /** What the group is called, since the two places asking differ. */
  readonly legend: string
  /**
   * Radio groups need a name of their own, or two on one page share a
   * selection — which on the join screen would mean the account form and the
   * guest form fighting over one answer.
   */
  readonly name: string
  /** Kept to a height on a phone, where 25 of them would bury the button. */
  readonly scrolls?: boolean
}

/**
 * Which teddy somebody is, drawn as a row of them.
 *
 * Extracted from the accounts page so the join screen can ask the same
 * question the same way. Two copies of a picker is how they come to disagree —
 * the same reasoning as `PickerCard` and `AppHeader`, and the same reason a
 * cousin's choice should look and behave like a household member's.
 *
 * A radio group rather than buttons, so a keyboard moves through it with the
 * arrows for free and the browser says how many there are and which is
 * chosen. The radio itself is `sr-only`, which is why the focus ring is drawn
 * on the label: without that it lands on something nobody can see — reachable
 * by keyboard and invisible to the person using one.
 */
export function TeddyChoice({ chosen, onChoose, legend, name, scrolls }: TeddyChoiceProps) {
  return (
    <fieldset className="flex min-w-0 flex-col gap-2 border-0 p-0">
      <legend className="font-display text-(length:--text-small) mb-1">{legend}</legend>

      {/*
        Scrolls within its own box where it is asked to. Twenty-five teddies on
        a phone would push the button that actually joins the game off the
        bottom of the screen, which is a worse problem than a short list.
      */}
      <div
        className={`flex flex-wrap gap-2 ${
          scrolls ? 'max-h-[40vh] overflow-y-auto [scrollbar-gutter:stable]' : ''
        }`}
      >
        {ALL_AVATARS.map((teddy) => (
          <label
            key={teddy.key}
            className={[
              'flex cursor-pointer flex-col items-center gap-1 rounded-(--radius-control) p-2',
              'has-[:focus-visible]:outline-(length:--width-focus) has-[:focus-visible]:outline-focus has-[:focus-visible]:outline-offset-(--offset-focus)',
              chosen === teddy.key ? 'bg-surface-sunken ring-primary ring-2' : '',
            ].join(' ')}
          >
            <input
              type="radio"
              name={name}
              value={teddy.key}
              checked={chosen === teddy.key}
              onChange={() => onChoose(teddy.key)}
              className="sr-only"
            />
            <Avatar teddy={teddy.key} name={teddy.name} size="small" />
            <span className="text-(length:--text-small)">{teddy.name}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}
